/**
 * Everything the UI needs from "the outside world", behind one interface so the whole app can
 * run offline against fixtures in demo mode (`/demo`). Screens get it from `useBackend()`.
 */
import type { Command, GameMap } from '@seven-dawns/shared';
import { enablePush, type PushSupport } from '../pwa/push';
import { api, type Api, type JoinRequestStatus } from './api';
import { createIdbViewCache, type ViewCache } from './viewCache';
import {
  createMatchMemory,
  createProfileStore,
  createSessionStore,
  type MatchMemory,
  type MatchSession,
  type PendingJoin,
  type ProfileStore,
  type SessionStore,
} from './session';
import { GameSocket, openRequestSocket, socketUrl, type CommandAck, type GameSocketHandlers } from './socket';

/** A live connection to one match (the WebSocket in live mode, a fake in demo mode). */
export interface MatchTransport {
  connect(): void;
  close(): void;
  send(cmd: Command): Promise<CommandAck>;
}

export interface Backend {
  mode: 'live' | 'demo';
  api: Api;
  sessions: SessionStore;
  profile: ProfileStore;
  memory: MatchMemory;
  openMatch(session: MatchSession, handlers: GameSocketHandlers): MatchTransport;
  loadMap(session: MatchSession, signal?: AbortSignal): Promise<GameMap>;
  /** Follow a late-join request until it is decided. Returns a stop function. */
  watchJoinRequest(pending: PendingJoin, onStatus: (status: JoinRequestStatus) => void): () => void;
  /** Ask for push permission and subscribe this device. */
  enablePush?(session: MatchSession): Promise<PushSupport>;
  /** Last view per match on this device, shown read-only when the server can't be reached. */
  viewCache?: ViewCache;
}

const POLL_MS = 15_000;

/**
 * Join-request watcher: the request socket answers immediately and again on decision; polling
 * every 15 s covers networks where WebSockets fail.
 */
function watchJoinRequestLive(pending: PendingJoin, onStatus: (status: JoinRequestStatus) => void) {
  let stopped = false;
  let ws: WebSocket | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const abort = new AbortController();

  const report = (status: JoinRequestStatus) => {
    if (stopped) return;
    onStatus(status);
    if (status.status !== 'pending') stop();
  };

  const poll = async () => {
    try {
      report(await api.joinRequestStatus(pending.requestId, pending.requestToken, abort.signal));
    } catch (error) {
      // 401: the request no longer exists (expired) — treat as rejected.
      if (error instanceof Error && 'status' in error && (error as { status: number }).status === 401) {
        report({ status: 'rejected' });
      }
    }
    if (!stopped) timer = setTimeout(() => void poll(), POLL_MS);
  };

  try {
    ws = openRequestSocket(socketUrl(), pending.requestToken, report);
  } catch {
    // WebSocket unavailable: rely on polling.
  }
  void poll();

  function stop() {
    stopped = true;
    abort.abort();
    if (timer) clearTimeout(timer);
    ws?.close(1000);
  }
  return stop;
}

export const liveBackend: Backend = {
  mode: 'live',
  api,
  sessions: createSessionStore(),
  profile: createProfileStore(),
  memory: createMatchMemory(),
  openMatch: (session, handlers) =>
    new GameSocket(socketUrl(), handlers, undefined, { t: 'auth', token: session.playerToken }),
  loadMap: (session, signal) => api.getMap(session.code, session.playerToken, signal),
  watchJoinRequest: watchJoinRequestLive,
  enablePush: (session) => enablePush(session.playerToken),
  viewCache: createIdbViewCache(),
};
